import React, { useState } from 'react';
import './DeleteButton.scss';
import DateRender from './DateRender';
import { AddShowingRequest, ShowingDetails } from '../models/api';
import DeleteButton from './DeleteButton';
import {
  add as addShowing,
  list as listShowings,
  remove as removeShowing,
  update as updateShowing
} from '../services/showing.service';
import ModalForm from './Modal';
import ShowingsForm from './ShowingsForm';
import { Link, useNavigate } from 'react-router-dom';
import { isExpired, isPast } from '../helpers/dates';
import { ReactComponent as CalendarTodayIcon } from '../assets/calendar_today_large.svg';
import { ReactComponent as LocationOnIcon } from '../assets/location_on.svg';
import Button from './Button';
import { FaPlus } from 'react-icons/fa';
import ShowingContextMenu from './ShowingContextMenu';

type ShowingEditorProps = {
  showings: ShowingDetails[];
  reference: string;
  setLoadMessage: (message: string) => void;
  setShowings: (showings: ShowingDetails[]) => void;
  setErrors: (errors: string[]) => void;
  refreshShowings: () => Promise<void>;
};

const ShowingEditor: React.FC<ShowingEditorProps> = ({
  showings,
  reference,
  setLoadMessage,
  setShowings,
  setErrors,
  refreshShowings
}) => {
  const [addErrors, setAddErrors] = useState([] as string[]);
  const [isAddingShowing, setIsAddingShowing] = useState(false);
  const [addShowingLoading, setAddShowingLoading] = useState(false);
  const [isShowingDeleting, setIsShowingDeleting] = useState(false);
  const [editingShowing, setEditingShowing] = useState<ShowingDetails | null>(
    null
  );
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState([] as string[]);

  const navigate = useNavigate();

  const toggleMenu = (index: number) => {
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };
  const handleDeleteShowing = async (showing: ShowingDetails) => {
    if (isShowingDeleting) {
      return;
    }
    setIsShowingDeleting(true);

    const result = await removeShowing(reference, showing.reference);
    if (result.success) {
      const showingsResponse = await listShowings(reference);
      if (showingsResponse.success) {
        setShowings(showingsResponse.data?.showings || []);
        setLoadMessage('Showing deleted');
        setErrors([]);
        setOpenMenuIndex(null);
      } else {
        setErrors(showingsResponse.errors);
      }
    } else {
      setLoadMessage('');
      setErrors(['There was a problem']);
    }
    setIsShowingDeleting(false);
  };

  const handleAddShowing = async (request: AddShowingRequest) => {
    if (!request.startDateUtc) {
      setAddErrors(['Start date is required']);
      return false;
    }

    setAddShowingLoading(true);

    try {
      if (editingShowing) {
        const result = await updateShowing(
          reference,
          editingShowing.reference,
          request
        );
        if (result.success && result.data) {
          setLoadMessage('Showing updated');
          setIsAddingShowing(false);
          setEditingShowing(null);
          await refreshShowings();
          return true;
        } else {
          setAddErrors(result.errors);
          return false;
        }
      } else {
        const result = await addShowing(reference, request);
        if (result.success && result.data) {
          setLoadMessage('Showing added');
          setIsAddingShowing(false);
          await refreshShowings();
          return true;
        } else {
          setAddErrors(result.errors);
          return false;
        }
      }
    } finally {
      setAddShowingLoading(false);
    }
  };

  const getStatus = (showing: ShowingDetails) => {
    if (showing.endDateUtc) {
      return 'Ended';
    }
    if (showing.presentationStartUtc) {
      return 'Presenting';
    }
    if (isExpired(showing.startDateUtc)) {
      return 'Expired';
    }
    return 'Upcoming';
  };

  return (
    <>
      <h2>Showings</h2>
      <div className="page-intro">
        <p>
          Showings are used to let you present the same event multiple times, to
          different audiences (e.g., different dates/times/locations).
        </p>
        <div className="actions">
          <Button onClick={() => setIsAddingShowing(true)}>
            <FaPlus />
            Add showing
          </Button>
        </div>
      </div>
      {showings.length === 0 && (
        <p>
          You haven&apos;t added any showings for this event yet. If you want to
          present this event multiple times, create a new showing for each
          date/time/location and you&apos;ll be able to present each
          individually.
        </p>
      )}
      {showings.length > 0 && (
        <div className="card-container">
          {showings.map((p, index) => (
            <div className="card" key={p.reference}>
              <Link
                to={`/Sessions/${reference}/Present/${p.reference}`}
                className="card__body"
              >
                <div className="title">
                  <DateRender dateString={p.startDateUtc} />
                </div>
                <div className="details">
                  {p.location && (
                    <div className="icon-row">
                      <LocationOnIcon />
                      {p.location}
                    </div>
                  )}
                </div>
                <div className="tags">
                  <div className="tag">{getStatus(p)}</div>
                </div>
              </Link>
              <ShowingContextMenu
                deleteLoading={deleteLoading}
                index={index}
                navigateToPresent={(p) =>
                  navigate(`/Sessions/${reference}/Present/${p.reference}`)
                }
                onDelete={(p) => handleDeleteShowing(p)}
                onEdit={(p) => {
                  setEditingShowing(p);
                  setIsAddingShowing(true);
                }}
                onToggle={(index) => toggleMenu(index)}
                selectedIndex={openMenuIndex}
                showingDetails={p}
              />
            </div>
          ))}
        </div>
      )}

      {isAddingShowing && (
        <ModalForm>
          <ShowingsForm
            errors={addErrors}
            isSubmitting={addShowingLoading}
            model={editingShowing}
            submit={handleAddShowing}
            close={() => {
              setIsAddingShowing(false);
              setEditingShowing(null);
              setAddErrors([]);
            }}
            buttonText={editingShowing ? 'Update' : 'Add'}
          />
        </ModalForm>
      )}
    </>
  );
};

export default ShowingEditor;
